/**
 * Lifecycle State Actions Component
 * @implements FS-002.5 - Project Lifecycle States
 * @implements BS-002.9 - Project Archival
 * @implements BS-002.10 - Project Deletion
 * @implements US-016 - Project Archival
 * @implements US-017 - Project Deletion
 * @implements TS-065 - Archive/Unarchive UI
 * @implements TS-070 - Delete Project UI
 *
 * Renders lifecycle action buttons based on the current lifecycle state:
 * - ACTIVE + NORMAL control state: "Archive Project" and "Delete Project" buttons
 * - ARCHIVED + NORMAL control state: "Unarchive Project" and "Delete Project" buttons
 * - PENDING_DELETION: No actions available (read-only)
 */

import { useState, useEffect } from 'react';
import { Button, Box, Tooltip, CircularProgress, Alert } from '@mui/material';
import {
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import { useMutation, gql } from '@apollo/client';
import { ArchiveProjectDialog } from './ArchiveProjectDialog';
import { DeleteProjectDialog } from './DeleteProjectDialog';
import type { LifecycleState, ControlState } from './StateBadge';
import { getReauthCookie, getPendingReauthAction } from '@/components/auth';

/**
 * GraphQL mutation for archiving a project
 * @implements TS-065 - Archive/Unarchive UI
 */
const ARCHIVE_PROJECT_MUTATION = gql`
  mutation ArchiveProject($projectId: ID!) {
    archiveProject(projectId: $projectId) {
      project {
        id
        lifecycleState
        controlState
      }
      patch {
        op
        path
        value
      }
    }
  }
`;

/**
 * GraphQL mutation for unarchiving a project
 * @implements TS-065 - Archive/Unarchive UI
 */
const UNARCHIVE_PROJECT_MUTATION = gql`
  mutation UnarchiveProject($projectId: ID!) {
    unarchiveProject(projectId: $projectId) {
      project {
        id
        lifecycleState
        controlState
      }
      patch {
        op
        path
        value
      }
    }
  }
`;

/**
 * Error code mapping for user-friendly error messages
 * @implements TS-065 - Archive/Unarchive UI
 * @implements TS-070 - Delete Project UI
 */
const ERROR_MESSAGES: Record<string, string> = {
  INVALID_LIFECYCLE_STATE_FOR_ARCHIVE:
    'This project cannot be archived in its current state.',
  INVALID_LIFECYCLE_STATE_FOR_UNARCHIVE:
    'This project cannot be unarchived in its current state.',
  INVALID_LIFECYCLE_STATE_FOR_DELETION:
    'This project cannot be deleted in its current state.',
  PROJECT_NOT_IN_NORMAL_STATE:
    'Project must be in Normal control state. Please complete or discard any pending changes first.',
  PROJECT_NOT_FOUND: 'Project not found.',
  UNAUTHORIZED: 'You do not have permission to perform this action.',
};

/**
 * Extract user-friendly error message from GraphQL error
 */
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    // Check for known error codes in the message
    for (const [code, message] of Object.entries(ERROR_MESSAGES)) {
      if (error.message.includes(code)) {
        return message;
      }
    }
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * Props for LifecycleStateActions component
 */
export interface LifecycleStateActionsProps {
  /** The project ID */
  projectId: string;
  /** The project title (for dialog display) */
  projectTitle: string;
  /** Current lifecycle state of the project */
  lifecycleState: LifecycleState;
  /** Current control state of the project */
  controlState: ControlState;
  /** Size of the buttons */
  size?: 'small' | 'medium' | 'large';
  /** Whether to show icons on buttons */
  showIcons?: boolean;
  /** Callback when an action starts */
  onActionStart?: () => void;
  /** Callback when an action completes successfully */
  onActionComplete?: () => void;
  /** Callback when an action fails */
  onActionError?: (error: string) => void;
  /** Callback when project is deleted (for navigation) */
  onDeleted?: () => void;
}

/**
 * Lifecycle State Actions Component
 * @implements TS-065 - Archive/Unarchive UI
 * @implements TS-070 - Delete Project UI
 *
 * Displays archive/unarchive/delete buttons based on current lifecycle and control state.
 * Handles loading states, error display, and confirmation dialogs.
 *
 * Business Rules:
 * - Archive button: visible when project is ACTIVE and control state is NORMAL
 * - Unarchive button: visible when project is ARCHIVED
 * - Delete button: visible when project is ACTIVE or ARCHIVED and control state is NORMAL
 * - Only product_owner role can perform these actions (enforced by backend)
 *
 * @example
 * ```tsx
 * <LifecycleStateActions
 *   projectId={project.id}
 *   projectTitle={project.title}
 *   lifecycleState={project.lifecycleState}
 *   controlState={project.controlState}
 *   showIcons
 *   onDeleted={() => navigate('/projects')}
 * />
 * ```
 */
export const LifecycleStateActions = ({
  projectId,
  projectTitle,
  lifecycleState,
  controlState,
  size = 'medium',
  showIcons = true,
  onActionStart,
  onActionComplete,
  onActionError,
  onDeleted,
}: LifecycleStateActionsProps) => {
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Archive mutation
  const [archiveProject, { loading: archiveLoading }] = useMutation(
    ARCHIVE_PROJECT_MUTATION,
    {
      variables: { projectId },
      onCompleted: () => {
        setArchiveDialogOpen(false);
        setError(null);
        onActionComplete?.();
      },
      onError: (err) => {
        const message = getErrorMessage(err);
        setError(message);
        onActionError?.(message);
      },
      // Update Apollo cache with the response
      update: (cache, { data }) => {
        if (data?.archiveProject?.project) {
          cache.modify({
            id: cache.identify({ __typename: 'Project', id: projectId }),
            fields: {
              lifecycleState: () => data.archiveProject.project.lifecycleState,
              controlState: () => data.archiveProject.project.controlState,
            },
          });
        }
      },
    },
  );

  // Unarchive mutation
  const [unarchiveProject, { loading: unarchiveLoading }] = useMutation(
    UNARCHIVE_PROJECT_MUTATION,
    {
      variables: { projectId },
      onCompleted: () => {
        setError(null);
        onActionComplete?.();
      },
      onError: (err) => {
        const message = getErrorMessage(err);
        setError(message);
        onActionError?.(message);
      },
      // Update Apollo cache with the response
      update: (cache, { data }) => {
        if (data?.unarchiveProject?.project) {
          cache.modify({
            id: cache.identify({ __typename: 'Project', id: projectId }),
            fields: {
              lifecycleState: () =>
                data.unarchiveProject.project.lifecycleState,
              controlState: () => data.unarchiveProject.project.controlState,
            },
          });
        }
      },
    },
  );

  /**
   * Auto-open delete dialog if returning from OAuth re-auth
   * @implements TS-070 - Delete Project UI (OAuth re-auth flow)
   *
   * When returning from OAuth re-auth, check if there's a pending
   * delete action for this project. If so, auto-open the delete dialog.
   * The DeleteProjectDialog will then see the reauth cookie and auto-proceed.
   */
  useEffect(() => {
    const reauthCookie = getReauthCookie();
    const pendingAction = getPendingReauthAction();

    // Check if this is a valid OAuth re-auth callback for this project
    if (
      reauthCookie &&
      pendingAction &&
      pendingAction.action === 'delete_project' &&
      pendingAction.projectId === projectId
    ) {
      // Auto-open the delete dialog
      setDeleteDialogOpen(true);
    }
  }, [projectId]);

  const isLoading = archiveLoading || unarchiveLoading;

  // Determine which actions are available based on state
  // Note: Backend returns lowercase values, so we normalize for comparison
  const normalizedLifecycle = lifecycleState?.toUpperCase();
  const normalizedControl = controlState?.toUpperCase();
  const isActive = normalizedLifecycle === 'ACTIVE';
  const isArchived = normalizedLifecycle === 'ARCHIVED';
  const isPendingDeletion = normalizedLifecycle === 'PENDING_DELETION';
  const isNormalControlState = normalizedControl === 'NORMAL';

  // Archive is only available for ACTIVE projects in NORMAL control state
  const canArchive = isActive && isNormalControlState;
  // Unarchive is only available for ARCHIVED projects
  const canUnarchive = isArchived;
  // Delete is available for ACTIVE or ARCHIVED projects in NORMAL control state
  // @implements TS-070 - Delete Project UI
  const canDelete = (isActive || isArchived) && isNormalControlState;
  // Show why delete is disabled when not in NORMAL control state
  const deleteDisabledReason =
    !isNormalControlState && !isPendingDeletion
      ? 'Project must be in Normal control state to delete. Complete or discard pending changes first.'
      : null;

  /**
   * Handle archive button click - show confirmation dialog
   */
  const handleArchiveClick = () => {
    setError(null);
    setArchiveDialogOpen(true);
  };

  /**
   * Handle archive confirmation
   */
  const handleArchiveConfirm = () => {
    onActionStart?.();
    archiveProject();
  };

  /**
   * Handle unarchive button click - no confirmation needed
   */
  const handleUnarchiveClick = () => {
    setError(null);
    onActionStart?.();
    unarchiveProject();
  };

  /**
   * Handle delete button click - show delete confirmation dialog
   * @implements TS-070 - Delete Project UI
   */
  const handleDeleteClick = () => {
    setError(null);
    setDeleteDialogOpen(true);
  };

  /**
   * Handle successful project deletion
   * @implements TS-070 - Delete Project UI
   */
  const handleDeleted = () => {
    setDeleteDialogOpen(false);
    onDeleted?.();
  };

  // If no actions are available, render nothing
  if (!canArchive && !canUnarchive && !canDelete && !deleteDisabledReason) {
    return null;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {/* Archive button - for ACTIVE + NORMAL projects */}
        {canArchive && (
          <Tooltip title="Archive this project (makes it read-only)">
            <span>
              <Button
                variant="outlined"
                color="inherit"
                size={size}
                onClick={handleArchiveClick}
                disabled={isLoading}
                startIcon={showIcons ? <ArchiveIcon /> : undefined}
                sx={{ color: 'text.secondary' }}
              >
                Archive Project
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Unarchive button - for ARCHIVED projects */}
        {canUnarchive && (
          <Tooltip title="Unarchive this project (restore editability)">
            <span>
              <Button
                variant="contained"
                color="primary"
                size={size}
                onClick={handleUnarchiveClick}
                disabled={isLoading}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : showIcons ? (
                    <UnarchiveIcon />
                  ) : undefined
                }
              >
                {isLoading ? 'Unarchiving...' : 'Unarchive Project'}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Delete button - for ACTIVE or ARCHIVED projects in NORMAL control state */}
        {/* @implements TS-070 - Delete Project UI */}
        {(canDelete || deleteDisabledReason) && (
          <Tooltip
            title={
              deleteDisabledReason ||
              'Permanently delete this project (30-day cooling-off period)'
            }
          >
            <span>
              <Button
                variant="outlined"
                color="error"
                size={size}
                onClick={handleDeleteClick}
                disabled={isLoading || !canDelete}
                startIcon={showIcons ? <DeleteForeverIcon /> : undefined}
              >
                Delete Project
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Archive confirmation dialog */}
      <ArchiveProjectDialog
        open={archiveDialogOpen}
        projectTitle={projectTitle}
        onClose={() => setArchiveDialogOpen(false)}
        onConfirm={handleArchiveConfirm}
        isLoading={archiveLoading}
      />

      {/* Delete confirmation dialog */}
      {/* @implements TS-070 - Delete Project UI */}
      <DeleteProjectDialog
        open={deleteDialogOpen}
        projectId={projectId}
        projectTitle={projectTitle}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={handleDeleted}
      />
    </Box>
  );
};
